import Page from '../../../shared/components/Page/Page';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, DatePicker, Button, Space, Tag, message, Statistic, Breadcrumb } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';
import { formatDateTime, numberFormat } from '../../../shared/utils/helpers';
import { getPartnerStatistics } from '../../partner/api';
import moment from 'moment';

const { RangePicker } = DatePicker;

function PartnerStatistic() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [dateRange, setDateRange] = useState([
        moment().subtract(30, 'days'),
        moment()
    ]);

    const fetchStatistics = async () => {
        setLoading(true);
        try {
            const response = await getPartnerStatistics( {
                userId: parseInt(userId),
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            });
            
            if (response.success) {
                setData(response.data);
            } else {
                message.error(response.message || 'Lỗi khi lấy thống kê');
            }
        } catch (error) {
            message.error('Có lỗi xảy ra khi tải dữ liệu');
        }
        setLoading(false);
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange(dates);
        }
    };

    const handleSearch = () => {
        fetchStatistics();
    };

    useEffect(() => {
        fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const columns = [
        {
            title: 'Ngày',
            dataIndex: 'date',
            key: 'date',
            align: 'center',
            render: (value) => formatDateTime(value, 'd')
        },
        {
            title: 'Tiền nạp',
            dataIndex: 'depositAmount',
            key: 'depositAmount',
            align: 'center',
            render: (value) => <Tag color="green">{numberFormat(value)}</Tag>
        },
        {
            title: 'Phí nạp',
            dataIndex: 'depositFee',
            key: 'depositFee',
            align: 'center',
            render: (value) => <Tag color="blue">{numberFormat(value)}</Tag>
        },
        {
            title: 'Số lần nạp',
            dataIndex: 'depositCount',
            key: 'depositCount',
            align: 'center',
            render: (value) => <Tag>{value}</Tag>
        },
        {
            title: 'Tiền rút',
            dataIndex: 'withdrawAmount',
            key: 'withdrawAmount',
            align: 'center',
            render: (value) => <Tag color="orange">{numberFormat(value)}</Tag>
        },
        {
            title: 'Phí rút',
            dataIndex: 'withdrawFee',
            key: 'withdrawFee',
            align: 'center',
            render: (value) => <Tag color="purple">{numberFormat(value)}</Tag>
        },
        {
            title: 'Số lần rút',
            dataIndex: 'withdrawCount',
            key: 'withdrawCount',
            align: 'center',
            render: (value) => <Tag>{value}</Tag>
        },
        {
            title: 'Tiền trừ',
            dataIndex: 'deductionFee',
            key: 'deductionFee',
            align: 'center',
            render: (value) => <Tag color="red">{numberFormat(value)}</Tag>
        },
        {
            title: 'Số lần trừ',
            dataIndex: 'deductionCount',
            key: 'deductionCount',
            align: 'center',
            render: (value) => <Tag>{value}</Tag>
        },
        {
            title: 'Lợi nhuận',
            dataIndex: 'profit',
            key: 'profit',
            align: 'center',
            render: (value) => (
                <Tag color={value >= 0 ? 'green' : 'red'}>
                    {numberFormat(value)}
                </Tag>
            )
        }
    ];

    return (
        <Page title="Thống kê đối tác">
            <div style={{ marginBottom: 16 }}>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <Button 
                            type="link" 
                            onClick={() => navigate('/admin/partner')}
                            icon={<ArrowLeftOutlined />}
                        >
                            Quay lại danh sách đối tác
                        </Button>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Thống kê đối tác</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            {data?.partnerInfo && (
                <Card 
                    title={
                        <Space>
                            <UserOutlined />
                            {`Thống kê đối tác: ${data.partnerInfo.name} (${data.partnerInfo.username})`}
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Row gutter={16}>
                        <Col span={6}>
                            <Tag color="blue" style={{ padding: '8px 16px', fontSize: '14px' }}>
                                ID: {data.partnerInfo.userId}
                            </Tag>
                        </Col>
                        <Col span={6}>
                            <Tag color="green" style={{ padding: '8px 16px', fontSize: '14px' }}>
                                Vai trò: {data.partnerInfo.role === 'partner' ? 'Đối tác chính' : 'Đối tác con'}
                            </Tag>
                        </Col>
                        <Col span={12}>
                            <Tag color="gold" style={{ padding: '8px 16px', fontSize: '14px' }}>
                                <DollarOutlined /> Số dư: {numberFormat(parseFloat(data.partnerInfo.balance || 0))}
                            </Tag>
                        </Col>
                    </Row>
                </Card>
            )}

            <Card 
                title={
                    <Space>
                        <CalendarOutlined />
                        Bộ lọc thời gian
                    </Space>
                }
                style={{ marginBottom: 16 }}
            >
                <Space>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        placeholder={['Từ ngày', 'Đến ngày']}
                    />
                    <Button 
                        type="primary" 
                        onClick={handleSearch}
                        loading={loading}
                    >
                        Tìm kiếm
                    </Button>
                </Space>
            </Card>

            {data?.summary && (
                <Card title="Thống kê tổng hợp" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Statistic
                                title="Tổng tiền nạp"
                                value={data.summary.totalDeposit || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: '#3f8600' }}
                                suffix="VNĐ"
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Tổng tiền rút"
                                value={data.summary.totalWithdraw || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: '#cf1322' }}
                                suffix="VNĐ"
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Tổng phí nạp"
                                value={data.summary.totalDepositFee || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: '#1890ff' }}
                                suffix="VNĐ"
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Tổng phí rút"
                                value={data.summary.totalWithdrawFee || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: '#722ed1' }}
                                suffix="VNĐ"
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Tổng tiền trừ"
                                value={data.summary.totalDeductionFee || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: '#f5222d' }}
                                suffix="VNĐ"
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Tổng lợi nhuận"
                                value={data.summary.totalProfit || 0}
                                formatter={(value) => numberFormat(value)}
                                valueStyle={{ color: (data.summary.totalProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                                suffix="VNĐ"
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            <Card title="Thống kê theo ngày">
                <Table
                    columns={columns}
                    dataSource={data?.dailyStats || []}
                    loading={loading}
                    rowKey="date"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
                    }}
                    scroll={{ x: 1200 }}
                    bordered
                    locale={{
                        emptyText: 'Không có dữ liệu trong khoảng thời gian này'
                    }}
                />
            </Card>
        </Page>
    );
}

export default PartnerStatistic; 
